import React, { FC } from 'react';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import { ClassNamesFn } from 'classnames/types';

import Text from '../../../atoms/Text';
import properties from '../../../../modules/ajio-util/properties';
import styles from './GiftWithPurchase.pcss';

import {
  ConfirmedEntry,
  CancelledEntry,
  ConsignmentsDatum,
  Entry2
} from '../../../types/ordersData';
import { RootState } from '../../../types/rootState';
import {
  getGiftWithProductConfig,
  getGiftWithProductEnable
} from '../../../selectors/featureFlagsSelector';
import { replaceWithJioCDN } from '../../../../utils/imageHandling';

interface CancelPopupDataType {
  orgQuantity: number;
  availableQty: number;
}

interface GiftWithPurchaseProps {
  entries: ConfirmedEntry[] | CancelledEntry[] | ConsignmentsDatum[] | Entry2[];
  currentItem: ConfirmedEntry | CancelledEntry | ConsignmentsDatum | Entry2;
  cancelPopup?: boolean;
  cancelPopupData?: CancelPopupDataType;
  isDeliveredOrdered?: boolean;
  giftItemsForCancalledEntries: CancelledEntry[];
  orderState: string;
  isReturnScreens: boolean;
  isReturnDetailScreen: boolean;
  isReturnListScreen: boolean;
  customClassNameForGWP: ClassNamesFn;
  isReturnRequestConfirmed: boolean;
  isGiftWithProductFlagOn: boolean;
  isDesktop: boolean;
  isDesktopCancelModal: boolean;
  returnEntriesForGift: boolean;
  isReturnSelectItemsDesktop: boolean;
  isReturnItemDetailsDesktop: boolean;
  isReturnVerifyScreenDesktop: boolean;
  isReturnRequestCofirmedDesktop: boolean;
  giftWithProductConfig: Record<string, string>;
  hideGifts: boolean;
  shouldShowGwpReturnText: boolean;
  shouldShowGwpCancelText: boolean;
  isReturnExchangeSegregated: boolean;
}

const cx = classNames.bind(styles);

export const getGiftItemData = ({
  giftItemsIdsString = '',
  entries,
  isDeliveredOrdered
}): { giftItemQty: number; giftItems: [] } => {
  const giftItemsIds = giftItemsIdsString.split(',').filter(cur => cur);

  return giftItemsIds.reduce(
    (acc, giftId) => {
      const giftItem = entries.find(
        ({ entry, orderEntry }) => (isDeliveredOrdered ? orderEntry : entry).product.code === giftId
      );
      if (!giftItem) return acc;

      acc = {
        ...acc,
        giftItemQty:
          acc.giftItemQty + giftItem?.[isDeliveredOrdered ? 'orderEntry' : 'entry'].quantity,
        giftItems: [...acc.giftItems, giftItem]
      };

      return acc;
    },
    { giftItemQty: 0, giftItems: [] }
  );
};

const mapStateToProps = (
  state: RootState
): { isGiftWithProductFlagOn: boolean; giftWithProductConfig: object } => {
  return {
    isGiftWithProductFlagOn: getGiftWithProductEnable(state?.flags),
    giftWithProductConfig: getGiftWithProductConfig(state)
  };
};

const connecter = connect(mapStateToProps);

const GiftWithPurchase: FC<GiftWithPurchaseProps> = props => {
  const {
    entries,
    currentItem,
    cancelPopup,
    cancelPopupData,
    isDeliveredOrdered,
    giftItemsForCancalledEntries,
    orderState,
    isReturnScreens,
    isReturnDetailScreen,
    isReturnListScreen,
    customClassNameForGWP = '',
    isReturnRequestConfirmed,
    isGiftWithProductFlagOn,
    // NOTE: FOR DESKTOP
    isDesktop,
    isDesktopCancelModal,
    returnEntriesForGift: returnEntriesForGiftDesktop,
    isReturnSelectItemsDesktop,
    isReturnItemDetailsDesktop,
    isReturnVerifyScreenDesktop,
    isReturnRequestCofirmedDesktop,
    giftWithProductConfig,
    hideGifts,
    shouldShowGwpReturnText,
    shouldShowGwpCancelText,
    isReturnExchangeSegregated = false
  } = props;

  if (!isGiftWithProductFlagOn) return null;
  if (hideGifts) return null;

  // For replacing count from firebase text
  const replaceCount = (title = '', count = 0): string => title.replace('${count}', `${count}`);

  // For Cancel Popup
  const { availableQty } = cancelPopupData ?? {};

  // For cancelled entries
  const giftEntryItems = orderState === 'CancelledEntries' ? giftItemsForCancalledEntries : entries;

  const gwpCodeForReturnConfirmed =
    isReturnRequestConfirmed &&
    entries.find(entry => entry.code === currentItem.productCode)?.orderEntry?.giftProducts;
  const currentItemGWPCodes = (() => {
    if (gwpCodeForReturnConfirmed) return gwpCodeForReturnConfirmed;

    if (isReturnDetailScreen) return currentItem?.giftProducts;

    return currentItem?.[isDeliveredOrdered ? 'orderEntry' : 'entry']?.giftProducts;
  })();

  const giftItemData = getGiftItemData({
    giftItemsIdsString: currentItemGWPCodes ?? '',
    entries: giftEntryItems,
    isDeliveredOrdered
  });
  const { giftItemQty, giftItems } = giftItemData;

  // If theres is no gift items present
  if (!giftItems.length) return null;

  // For showing freebies text
  const giftText = (() => {
    const {
      orderDetailsFreebieText = '',
      odpSingleFreebiesCancelText = '',
      odpMultipleFreebiesCancelText = '',
      odpSingleFreebiesText = '',
      odpMultipleFreebiesText = '',
      odpSingleReturnFreebiesText = '',
      odpMultipleReturnFreebiesText = '',
      odpSingleReturn2FreebiesText = '',
      odpMultipleReturn2FreebiesText = '',
      cancelModalSingleFreebiesText = '',
      cancelModalMultipleFreebiesText = ''
    } = giftWithProductConfig;

    if (cancelPopup) {
      if (availableQty === 1 || shouldShowGwpCancelText) {
        return giftItemQty > 1
          ? replaceCount(odpMultipleFreebiesCancelText, giftItemQty)
          : odpSingleFreebiesCancelText;
      }

      return giftItemQty > 1
        ? replaceCount(odpMultipleFreebiesText, giftItemQty)
        : odpSingleFreebiesText;
    }

    if (
      (isReturnScreens &&
        ((isReturnDetailScreen && shouldShowGwpReturnText) ||
          isReturnListScreen ||
          isReturnRequestConfirmed ||
          isReturnVerifyScreenDesktop ||
          isReturnRequestCofirmedDesktop)) ||
      returnEntriesForGiftDesktop
    )
      return giftItemQty > 1
        ? replaceCount(odpMultipleReturnFreebiesText, giftItemQty)
        : odpSingleReturnFreebiesText;

    if (isReturnScreens && (!isReturnDetailScreen || !shouldShowGwpReturnText))
      return giftItemQty > 1
        ? replaceCount(odpMultipleReturn2FreebiesText, giftItemQty)
        : odpSingleReturn2FreebiesText;

    if (giftItemsForCancalledEntries?.length && orderState === 'CancelledEntries') {
      return giftItemQty > 1
        ? replaceCount(cancelModalMultipleFreebiesText, giftItemQty)
        : cancelModalSingleFreebiesText;
    }

    return `${giftItemQty > 1 ? giftItemQty : ''} ${orderDetailsFreebieText}`;
  })();

  // For picking the key for Delivered and Confirmed orders
  const giftItemKey = isDeliveredOrdered ? 'orderEntry' : 'entry';

  const infoIcon = `${properties.getProperty(
    'imageBaseUrl'
  )}/static/img/giftWithProductInfoIcon.svg`;

  return (
    <div
      className={[
        cx('gift_with-product__container', isReturnDetailScreen ? 'return_details-screen' : ''),
        customClassNameForGWP,
        isDesktop ? cx('desktop') : '',
        orderState === 'CancelledEntries' ? cx('cancel_entries') : '',
        isDeliveredOrdered ? cx('delivered_order') : '',
        isDesktopCancelModal ? cx('desktop_cancel-modal') : '',
        returnEntriesForGiftDesktop ? cx('desktop_return-request') : '',
        isReturnSelectItemsDesktop ? cx('desktop_return-request__items') : '',
        isReturnItemDetailsDesktop ? cx('desktop_return-request__item_details') : '',
        isReturnVerifyScreenDesktop ? cx('desktop_return-request__verify_details') : '',
        isReturnRequestCofirmedDesktop ? cx('desktop_return-request__confirmed') : '',
        isReturnExchangeSegregated ? cx('promote-exchange-segregation') : ''
      ].join(' ')}
    >
      <div className={cx('gift_with-product__title')}>
        <img src={infoIcon ?? ''} alt="Info icon" />
        <Text variant="T2" color="Gift_with-purchase__text" weight="bold">
          {giftText}
        </Text>
      </div>
      <div className={cx('gift_with-product__body')}>
        {giftItems.map(({ [giftItemKey]: giftItem }) => (
          <div className={cx('gift_with-product__item')}>
            <img src={replaceWithJioCDN("https://assets-uat.ajio.ril.com/medias/sys_master/root/20211230/160749/61cd8bfd861d376bad8e4f9b/-288Wx360H-469519639-wine-MODEL.jpg")} />
            <div className={cx('gift_with-product__item-details')}>
              <Text variant="T2" color="Light_Grey_2">
                {`${giftItem?.product?.brandName} - ${giftItem?.product?.name}`}
              </Text>
              <Text customClass={cx('qty')} variant="T1" color="Light_Grey_2">
                Qty {giftItem?.quantity ?? 0}
              </Text>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default connecter(GiftWithPurchase);
